import React, { useMemo, useCallback } from 'react';
import { SyutGrid, type SyutGridColumn } from 'components/ui';
import { useDrill } from 'contexts/DrillContext';
import type { DrillDimension } from 'contexts/DrillContext';
import type { DrillRow } from 'api/types/drillReport.types';
import type { PaginatedResponse } from 'api/types';

interface Props {
  data: (DrillRow & { id: string })[];
  pagination: PaginatedResponse<any>['pagination'];
  currentPage: number;
  pageSize: number;
  onPageChange: (pageIndex: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  loading?: boolean;
  defaultDrillPath: DrillDimension[];
}

function formatMinutes(minutes: number): string {
  if (!minutes || minutes <= 0) return '\u2014';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

function formatCurrency(amount: number | null | undefined): string {
  if (amount == null || amount === 0) return '\u2014';
  return `\u20B9 ${Number(amount).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

const DIMENSION_HEADER: Record<string, string> = {
  Employee: 'Employee', Manager: 'Manager', Engagement: 'Engagement',
  Client: 'Client', EngagementType: 'Engagement Type', Activity: 'Activity',
  ChargeType: 'Charge Type', WorkDate: 'Date', Entry: 'Entry',
};

export const SyutDrillGrid: React.FC<Props> = ({
  data, pagination, currentPage, pageSize, onPageChange, onPageSizeChange, loading, defaultDrillPath,
}) => {
  const { state, drillIn, availableDimensions } = useDrill();
  const { currentGroupBy, breadcrumbs } = state;
  const isLeaf = currentGroupBy === 'Entry';

  // Determine the next groupBy when a row is clicked
  const getNextGroupBy = useCallback((): DrillDimension | null => {
    if (isLeaf) return null;
    const depth = breadcrumbs.length + 1;
    // Try default path at this depth; if it's already used, pick first available
    const nextDefault = defaultDrillPath[depth];
    if (nextDefault && availableDimensions.includes(nextDefault)) return nextDefault;
    // If availableDimensions has Entry at the end, prefer the one before Entry
    const nonEntry = availableDimensions.filter(d => d !== 'Entry');
    return nonEntry[0] || availableDimensions[0] || null;
  }, [isLeaf, breadcrumbs.length, defaultDrillPath, availableDimensions]);

  const handleRowDrill = useCallback((row: DrillRow) => {
    const nextGb = getNextGroupBy();
    if (!nextGb) return;
    drillIn(
      currentGroupBy as DrillDimension,
      row.groupKey,
      row.groupLabel || row.groupKey,
      nextGb,
    );
  }, [currentGroupBy, getNextGroupBy, drillIn]);

  const columns: SyutGridColumn<any>[] = useMemo(() => {
    const cols: SyutGridColumn<any>[] = [
      {
        id: 'groupLabel',
        header: DIMENSION_HEADER[currentGroupBy] || currentGroupBy,
        accessorKey: 'groupLabel',
        size: isLeaf ? 300 : 260,
        enableSorting: true,
        cell: ({ row }: any) => {
          const r = row.original as DrillRow;
          if (isLeaf) {
            return <span>{r.groupLabel}</span>;
          }
          return (
            <div className="drill-group-cell" onClick={() => handleRowDrill(r)}>
              <div>
                {r.groupLabel || r.groupKey}
                <span className="drill-chevron">▸</span>
              </div>
              {r.groupSubLabel && <div className="drill-sub-label">{r.groupSubLabel}</div>}
            </div>
          );
        },
      },
    ];

    // Leaf-level shows extra columns
    if (isLeaf) {
      cols.push(
        { id: 'workDate', header: 'Date', accessorKey: 'workDate', size: 100,
          cell: ({ row }: any) => {
            const d = row.original.workDate;
            if (!d) return '\u2014';
            const parts = d.split('T')[0].split('-').map(Number);
            const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
            return `${String(parts[2]).padStart(2,'0')} ${months[parts[1]-1]} ${parts[0]}`;
          }
        },
        { id: 'employeeName', header: 'Employee', accessorKey: 'employeeName', size: 140 },
        { id: 'activityName', header: 'Activity', accessorKey: 'activityName', size: 140 },
        { id: 'chargeTypeValue', header: 'Charge Type', accessorKey: 'chargeTypeValue', size: 110 },
      );
    }

    // Common metric columns
    cols.push(
      {
        id: 'totalMinutes', header: 'Hours', accessorKey: 'totalMinutes', size: 90,
        cell: ({ row }: any) => formatMinutes(row.original.totalMinutes),
      },
      {
        id: 'standardAmount', header: 'Standard \u20B9', accessorKey: 'standardAmount', size: 120,
        cell: ({ row }: any) => formatCurrency(row.original.standardAmount),
      },
      {
        id: 'discountedAmount', header: 'Discounted \u20B9', accessorKey: 'discountedAmount', size: 120,
        cell: ({ row }: any) => formatCurrency(row.original.discountedAmount),
      },
      {
        id: 'premiumAmount', header: 'Premium \u20B9', accessorKey: 'premiumAmount', size: 120,
        cell: ({ row }: any) => formatCurrency(row.original.premiumAmount),
      },
    );

    if (!isLeaf) {
      cols.push({
        id: 'entryCount', header: 'Entries', accessorKey: 'entryCount', size: 80,
      });
    }

    return cols;
  }, [currentGroupBy, isLeaf, handleRowDrill]);

  return (
    <div className="syut-drill-grid" style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <SyutGrid
        data={data}
        columns={columns}
        enablePagination
        manualPagination
        totalCount={pagination.totalItems}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        enableSorting
        emptyMessage={loading ? 'Loading...' : 'No data at this level.'}
      />
    </div>
  );
};

export default SyutDrillGrid;
